import { Link } from "@tanstack/react-router";
import {
  Trophy, CheckCircle2, XCircle, MinusCircle, Clock, Sparkles, Target,
  TrendingUp, BookOpen, Video, FileText, GraduationCap, MessageCircle,
  AlertCircle, ArrowRight, Award, Zap, Heart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { AdaptiveReport, MasteryLevel, AdaptiveTier } from "@/lib/adaptive-engine.functions";

const MASTERY_META: Record<MasteryLevel, { label: string; emoji: string; cls: string }> = {
  beginning:   { label: "Beginning",   emoji: "🔴", cls: "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30" },
  developing:  { label: "Developing",  emoji: "🟠", cls: "bg-orange-500/15 text-orange-600 dark:text-orange-400 border-orange-500/30" },
  proficient:  { label: "Proficient",  emoji: "🟡", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30" },
  mastered:    { label: "Mastered",    emoji: "🟢", cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30" },
};

const TIER_META: Record<AdaptiveTier, { gradient: string; icon: typeof Trophy; chip: string }> = {
  excellent: { gradient: "from-emerald-500/15 via-card to-gold/10", icon: Trophy,    chip: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
  great:     { gradient: "from-primary/15 via-card to-gold/10",    icon: Sparkles,  chip: "bg-primary/15 text-primary" },
  progress:  { gradient: "from-amber-500/15 via-card to-orange-500/10", icon: TrendingUp, chip: "bg-amber-500/15 text-amber-700 dark:text-amber-400" },
  recovery:  { gradient: "from-clay/20 via-card to-primary/10",    icon: Heart,     chip: "bg-clay/20 text-clay-foreground" },
};

function fmtTime(sec: number) {
  if (!sec || sec < 0) return "—";
  const m = Math.floor(sec / 60), s = sec % 60;
  return m > 0 ? `${m}m ${s.toString().padStart(2, "0")}s` : `${s}s`;
}

export function AdaptiveReportView({
  report,
  onCta,
  onRetry,
}: {
  report: AdaptiveReport;
  onCta?: () => void;
  onRetry?: () => void;
}) {
  const tier = TIER_META[report.tier];
  const TierIcon = tier.icon;
  const mastery = MASTERY_META[report.masteryLevel];
  const passed = report.percent >= 50;

  return (
    <div className="space-y-5">
      {/* Headline / score hero */}
      <div className={`rounded-3xl border border-border bg-gradient-to-br ${tier.gradient} p-6 shadow-soft`}>
        <div className="flex items-start gap-4">
          <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-card/80 backdrop-blur">
            <TierIcon className="h-7 w-7 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${tier.chip}`}>
              {report.tier}
            </div>
            <h2 className="mt-1 font-display text-2xl font-black leading-tight">{report.headline}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{report.narrative}</p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat icon={Target} label="Score" value={`${report.percent}%`} />
          <Stat icon={CheckCircle2} label="Correct" value={`${report.totals.correct}/${report.totals.total}`} />
          <Stat icon={Clock} label="Time" value={fmtTime(report.totals.durationSec)} />
          <Stat icon={Award} label="Status" value={passed ? "Passed" : "Keep going"} />
        </div>

        {/* Mastery + progress bar */}
        <div className="mt-5 flex items-center justify-between gap-3 rounded-2xl border border-border/60 bg-card/60 p-3">
          <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-bold ${mastery.cls}`}>
            <span>{mastery.emoji}</span> {mastery.label}
          </div>
          <div className="flex-1 max-w-md">
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div
                className={`h-full transition-all ${report.tier === "excellent" ? "bg-emerald-500" : report.tier === "great" ? "bg-primary" : report.tier === "progress" ? "bg-amber-500" : "bg-clay"}`}
                style={{ width: `${Math.max(4, report.percent)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Primary CTA */}
        <div className="mt-5 flex flex-wrap gap-2">
          <Button size="lg" className="flex-1 sm:flex-none" onClick={onCta}>
            <Zap className="mr-2 h-4 w-4" />
            {report.ctaLabel}
          </Button>
          {onRetry && (
            <Button size="lg" variant="outline" onClick={onRetry}>
              Try again
            </Button>
          )}
        </div>
      </div>

      {/* Correct / Incorrect / Skipped breakdown */}
      <div className="grid grid-cols-3 gap-3">
        <Tally icon={CheckCircle2} label="Correct"   value={report.totals.correct}   accent="text-emerald-600 dark:text-emerald-400" />
        <Tally icon={XCircle}      label="Incorrect" value={report.totals.incorrect} accent="text-clay" />
        <Tally icon={MinusCircle}  label="Skipped"   value={report.totals.skipped}   accent="text-muted-foreground" />
      </div>

      {/* AI Analysis blocks */}
      <div className="grid gap-4 md:grid-cols-2">
        <AnalysisCard title="Strong topics" tone="good" items={report.strongTopics} empty="Build strengths by practising regularly." />
        <AnalysisCard title="Areas to grow" tone="warn" items={report.weakTopics}    empty="No major weak topics detected. Keep going!" />
        <AnalysisCard title="Common mistakes" tone="neutral" items={report.commonMistakes} empty="No recurring mistake patterns spotted." />
        <AnalysisCard title="Knowledge gaps"  tone="neutral" items={report.knowledgeGaps}  empty="No major gaps — strong foundation." />
      </div>

      {(report.timeManagement || report.guessingFlag) && (
        <div className="rounded-2xl border border-border bg-muted/30 p-4 text-sm">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <div className="space-y-1">
              {report.timeManagement && <p><span className="font-semibold">Time:</span> {report.timeManagement}</p>}
              {report.guessingFlag && <p><span className="font-semibold">Note:</span> Some answers look like guesses — slowing down may help.</p>}
            </div>
          </div>
        </div>
      )}

      {/* Topic / difficulty breakdown */}
      {report.topicBreakdown.length > 0 && (
        <BreakdownPanel title="Topic breakdown" rows={report.topicBreakdown.map((r) => ({ label: r.topic, correct: r.correct, total: r.total, percent: r.percent }))} />
      )}
      {report.difficultyBreakdown.length > 0 && (
        <BreakdownPanel title="Difficulty breakdown" rows={report.difficultyBreakdown.map((r) => ({ label: r.difficulty, correct: r.correct, total: r.total, percent: r.percent }))} />
      )}

      {/* Smart recommendations */}
      <div className="rounded-3xl border border-border bg-card p-5 shadow-soft">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold">Recommended for you</h3>
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase text-primary">~{report.recommendations.estimatedMinutes} min</span>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <Rec icon={BookOpen}    label="Next lesson"    value={report.recommendations.nextLesson} />
          <Rec icon={Video}       label="Revision video" value={report.recommendations.revisionVideo} />
          <Rec icon={FileText}    label="Notes"          value={report.recommendations.notes} />
          <Rec icon={GraduationCap} label="Past papers"  value={report.recommendations.pastPapers} />
          <Rec
            icon={MessageCircle}
            label="Ask MAKAI AI"
            value={report.recommendations.tutorPrompt}
            to={`/learner/tutor?q=${encodeURIComponent(report.recommendations.tutorPrompt)}`}
          />
          <Rec icon={Target}      label="Confidence" value={report.recommendations.confidence.toUpperCase()} />
        </div>

        {report.nextActions.length > 0 && (
          <div className="mt-4 rounded-2xl bg-muted/40 p-3">
            <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Your next steps</div>
            <ul className="mt-1 space-y-1 text-sm">
              {report.nextActions.map((a, i) => (
                <li key={i} className="flex items-start gap-2"><ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" /> {a}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <Button asChild variant="outline" className="flex-1"><Link to="/learner/quizzes">Back to quizzes</Link></Button>
        <Button asChild className="flex-1"><Link to="/learner/tutor">Open MAKAI AI Tutor</Link></Button>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }: { icon: typeof Trophy; label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-border/50 bg-card/70 p-3 backdrop-blur">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="mt-0.5 font-display text-xl font-black">{value}</div>
    </div>
  );
}

function Tally({ icon: Icon, label, value, accent }: { icon: typeof Trophy; label: string; value: number; accent: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-3 text-center">
      <Icon className={`mx-auto h-5 w-5 ${accent}`} />
      <div className="mt-1 font-display text-2xl font-black">{value}</div>
      <div className="text-[10px] font-semibold uppercase text-muted-foreground">{label}</div>
    </div>
  );
}

function AnalysisCard({ title, items, empty, tone }: { title: string; items: string[]; empty: string; tone: "good" | "warn" | "neutral" }) {
  const dot = tone === "good" ? "bg-emerald-500" : tone === "warn" ? "bg-amber-500" : "bg-primary";
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="mb-2 flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${dot}`} />
        <h4 className="text-sm font-bold">{title}</h4>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">{empty}</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {items.map((x, i) => <li key={i} className="flex gap-1.5"><span className="text-muted-foreground">•</span> {x}</li>)}
        </ul>
      )}
    </div>
  );
}

function BreakdownPanel({ title, rows }: { title: string; rows: Array<{ label: string; correct: number; total: number; percent: number }> }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <h3 className="mb-3 text-sm font-bold">{title}</h3>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i}>
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="font-medium capitalize">{r.label || "—"}</span>
              <span className="text-muted-foreground">{r.correct}/{r.total} · {r.percent}%</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className={`h-full ${r.percent >= 75 ? "bg-emerald-500" : r.percent >= 50 ? "bg-amber-500" : "bg-clay"}`}
                style={{ width: `${Math.max(4, r.percent)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Rec({ icon: Icon, label, value, to }: { icon: typeof Trophy; label: string; value: string; to?: string }) {
  const body = (
    <>
      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/10"><Icon className="h-4 w-4 text-primary" /></div>
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="truncate text-sm font-medium">{value}</div>
      </div>
    </>
  );
  const cls = "flex items-center gap-2.5 rounded-xl border border-border bg-muted/30 p-2.5 transition hover:border-primary/40 hover:bg-muted/60";
  return to ? <Link to={to as never} className={cls}>{body}</Link> : <div className={cls}>{body}</div>;
}
